import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn } from 'lucide-react';
import StrategyDiagram from './StrategyDiagram';

/**
 * DiagramModal — full-screen deep-dive of a strategy diagram, layered over the
 * page with a dimmed backdrop, the diagram scaled up, and case-specific
 * annotated hotspots.
 *
 * Contract:
 *   <DiagramModal open slug hotspots title onClose />
 *
 * Hotspots are positioned in the SAME viewBox coordinates the diagram uses,
 * expressed as percentages of the diagram's frame (see `HOTSPOTS` below).
 */

// Per-slug hotspot annotations. Positions are % of the diagram frame (which
// preserves aspect on any container size). Each hotspot has a short title
// and a longer description surfaced on hover / focus.
export const HOTSPOTS = {
  wiiv: [
    { x: 46, y: 22, title: 'Client',        body: 'A single member-facing shell across mobile and web — one surface hosts every capability module on the platform.' },
    { x: 46, y: 40, title: 'API gateway',   body: 'A single ingress applies auth, rate limits and routing before any request reaches a downstream service.' },
    { x: 13, y: 51, title: 'Capability modules', body: 'Multiple capability groups — commerce, payments, rewards, concierge — coexist on the same surface without stepping on each other.' },
    { x: 88, y: 51, title: 'Concierge',     body: 'The assistant is a first-class module in the surface, not a bolt-on layer added later.' },
    { x: 13, y: 72, title: 'Core services', body: 'Identity, wallet, notifications and compliance are shared across every module — one profile per member, one audit trail.' },
    { x: 88, y: 87, title: 'Data platform', body: 'An event stream and behavioural spine sit under the core so every product decision has the same source of truth.' },
    { x: 46, y: 96, title: 'Assistant scope', body: 'The conversational assistant is scoped inside the platform — it reuses the same downstream APIs the capability modules do, so no separate integration path is needed.' },
  ],
  northladder: [
    { x: 18, y: 34, title: 'Buyer paths in',     body: 'Browse pooled inventory or post a requirement — both entry points route through the same anonymised engine.' },
    { x: 50, y: 34, title: 'Anonymised engine',  body: 'Unified pricing model, fractional supplier allocation, strict anonymity enforced across every buyer/supplier interaction.' },
    { x: 82, y: 34, title: 'Supplier console',   body: 'Matched pool sorted by price plus a demand-intelligence panel translating buyer routing signals back into supplier opportunities.' },
    { x: 50, y: 74, title: 'Demand-intelligence loop', body: 'Buyer signals become supplier-facing intelligence — a data flywheel that compounds without breaking anonymity.' },
    { x: 50, y: 92, title: 'Two-sided proof',    body: 'The pattern proves both sides work against a shared engine — before any counterparty conversation opens.' },
  ],
  sephora: [
    { x: 11, y: 34, title: 'Central',        body: 'Central vision that sets the strategic ceiling — brand, category framework, global commerce.' },
    { x: 89, y: 34, title: 'Global',         body: 'Global standards flow through central and get expressed locally, on the ground.' },
    { x: 50, y: 58, title: 'UAE market',     body: 'One strategy, expressed locally — search on Arabic keyboards, occasions that matter, loyalty behaviour reflecting how customers actually shop.' },
    { x: 12, y: 78, title: 'Central adapts', body: 'A working line between central and local teams — decisions taken quickly, at the right level, without a committee.' },
    { x: 88, y: 78, title: 'Local flexes',   body: 'Local teams move on customer moments without waiting for global sign-off — the operating model is designed for it.' },
  ],
  'al-futtaim': [
    { x: 50, y: 55, title: 'Blue hub',       body: 'The single customer surface — one identity, one loyalty balance, one payment layer across the group.' },
    { x: 50, y: 20, title: 'Automotive',     body: 'Toyota, Lexus, Honda — service journeys flow to the same Blue identity as the rest of the group.' },
    { x: 90, y: 55, title: 'Financial services', body: 'Blue FAB credit card, insurance — payments and credit inside the same shell as commerce.' },
    { x: 90, y: 30, title: 'Retail',         body: 'IKEA, M&S — retail journeys carry Blue loyalty balance across sessions and stores.' },
    { x: 10, y: 55, title: 'Health & Beauty', body: 'Watsons, Faces — cross-brand offers land in a single vouchers screen.' },
    { x: 10, y: 30, title: 'Fashion & Lifestyle', body: 'Zara, Trendyol, Robinsons — the group appears as one to the customer.' },
  ],
  magrabi: [
    { x: 50, y: 38, title: 'eCommerce',      body: 'Product discovery — frames, lenses, care. Any prescription started here follows the customer everywhere.' },
    { x: 50, y: 62, title: 'Customer file',  body: 'The single file every surface reads and writes to — no more prescription-lost-between-channels moments.' },
    { x: 50, y: 86, title: 'In-store',       body: 'Fitting, prescription capture, after-care — every store visit updates the same customer file.' },
  ],
  fenix: [
    { x: 50, y: 58, title: 'Digital spine',  body: 'The shared centre — release path, operating rhythms, shared services — that ten brands live inside.' },
    { x: 12, y: 82, title: 'Europe · 8 brands', body: 'The bulk of the constellation — heritage brands, retail chains, technical outdoor.' },
    { x: 60, y: 82, title: 'USA · 2 brands', body: 'Royal Robbins and Brunton — served by the same spine, kept independent where it matters.' },
    { x: 50, y: 20, title: 'Brand plurality', body: 'Ten distinct voices — the spine helps them, never flattens them.' },
  ],
  'digitalworks-internal': [
    { x: 14, y: 55, title: 'Coverage first', body: 'End-to-end tests earned by business stakeholders, not just engineers — the load-bearing foundation.' },
    { x: 50, y: 55, title: 'Two-person integrity', body: 'Every production release approved by two different people. Nothing ships on an agent’s authority.' },
    { x: 86, y: 55, title: 'Published cost', body: 'We shared the honest number — 4 months, 6 workstreams, and the places we struggled.' },
  ],
};

export default function DiagramModal({ open, slug, title, onClose }) {
  const spots = HOTSPOTS[slug] || [];

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    // Prevent body scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] bg-[#0A0A0A]/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={onClose}
          data-testid="diagram-modal-backdrop"
        >
          <motion.div
            key="dialog"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-[1180px] max-h-[92vh] bg-[#FBFAF6] rounded-[24px] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid={`diagram-modal-${slug}`}
            role="dialog"
            aria-modal="true"
            aria-label={`${title || 'Strategy diagram'} — deep dive`}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-[#E8E4D9]">
              <div>
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#178838]">Diagram deep-dive</div>
                <div className="serif text-[22px] md:text-[26px] leading-tight mt-1 text-[#1A1A1A]">{title || 'Strategy direction'}</div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close deep-dive"
                data-testid="diagram-modal-close"
                className="w-10 h-10 rounded-full border border-[#1A1A1A]/15 hover:border-[#1A1A1A]/35 hover:bg-[#F4F2EC] flex items-center justify-center transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Diagram + hotspots stage */}
            <div className="relative flex-1 overflow-auto bg-[#FBFAF6]">
              <div className="relative w-full">
                {/* The diagram itself, larger */}
                <div className="p-4 md:p-8">
                  <div className="relative w-full">
                    <StrategyDiagram slug={slug} />
                    {/* Hotspots overlay — sized to the SVG frame exactly so
                        percentage coords land on the diagram nodes. */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="relative w-full h-full">
                        {spots.map((h, i) => (
                          <Hotspot key={i} idx={i + 1} {...h} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer legend */}
            {spots.length > 0 && (
              <div className="px-6 md:px-10 py-4 border-t border-[#E8E4D9] bg-[#F4F2EC]">
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/60 mb-2">Annotated hotspots · hover a marker</div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12px] text-[#1A1A1A]/75">
                  {spots.map((h, i) => (
                    <div key={i} className="inline-flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#178838] text-[#FBFAF6] text-[10px] flex items-center justify-center font-medium">{i + 1}</span>
                      <span className="serif">{h.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Hotspot({ x, y, idx, title, body }) {
  const [hover, setHover] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const active = hover || focused;
  // Flip the tooltip to the top when the hotspot sits in the lower half —
  // stops the panel colliding with the hotspot below it in dense diagrams.
  const openUp = y > 60;
  return (
    <div
      className="absolute pointer-events-auto"
      style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}
    >
      <button
        type="button"
        aria-label={`Hotspot ${idx}: ${title}`}
        data-testid={`hotspot-${idx}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="relative w-8 h-8 rounded-full bg-[#178838] text-[#FBFAF6] text-[12px] font-medium flex items-center justify-center hover:scale-110 focus:scale-110 focus:outline-none focus:ring-2 focus:ring-[#178838]/40 transition-transform"
      >
        {idx}
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[#178838]/40 animate-ping" aria-hidden />
      </button>
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0, y: openUp ? -4 : 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: openUp ? -6 : 4, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className={`absolute left-1/2 -translate-x-1/2 w-[260px] rounded-[10px] bg-[#0A0A0A] text-[#FBFAF6] p-3 shadow-lg z-20 ${openUp ? 'bottom-full mb-3' : 'top-full mt-3'}`}
          >
            <div className="mono text-[9px] uppercase tracking-[0.24em] text-[#7ED4A0]">Hotspot {String(idx).padStart(2, '0')}</div>
            <div className="serif text-[15px] leading-tight mt-1">{title}</div>
            <div className="text-[11.5px] leading-[1.55] text-[#FBFAF6]/80 mt-2">{body}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function DiagramTriggerHint() {
  return (
    <div className="inline-flex items-center gap-2 mono text-[10px] tracking-[0.22em] uppercase text-[#178838]">
      <ZoomIn size={13} /> Click to deep-dive
    </div>
  );
}
